import { computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { getFreeGiftCartSku, useCartStore } from '@/features/Cart/stores/cart';
import { isFreeGiftOptionAvailable } from '@/features/Cart/cartRules/freeGift';
import { getProductBySku } from '@/features/Product/composables/productLookupHelper';
import type { FreeGiftChoice } from '@/features/Cart/types/models';

export const useFreeGiftOptionsStore = defineStore('freeGiftOptions', () => {
  const cartStore = useCartStore();
  const { getFreeGiftOption, isFreeGiftEligibleSku } = cartStore;
  const { freeGiftOptions, freeGiftItem } = storeToRefs(cartStore);

  const availableFreeGiftChoices = computed<FreeGiftChoice[]>(() =>
    freeGiftOptions.value.flatMap((option) => {
      const product =
        getProductBySku(option.displaySku) ?? getProductBySku(getFreeGiftCartSku(option));
      return product && isFreeGiftOptionAvailable(option) ? [{ option, product }] : [];
    }),
  );

  const cartFreeGiftSku = computed(() =>
    freeGiftItem.value ? getFreeGiftOption(freeGiftItem.value)?.displaySku : undefined,
  );

  const selectedFreeGiftChoice = computed<FreeGiftChoice | null>(
    () => availableFreeGiftChoices.value[0] ?? null,
  );

  const hasFreeGiftChooser = (sku: string): boolean =>
    isFreeGiftEligibleSku(sku) && availableFreeGiftChoices.value.length > 1;

  return {
    availableFreeGiftChoices,
    cartFreeGiftSku,
    selectedFreeGiftChoice,
    hasFreeGiftChooser,
  };
});
